import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  DocumentArrowUpIcon,
  DocumentTextIcon,
  TrashIcon,
  EyeIcon,
  PencilIcon,
  CloudArrowDownIcon,
  PlusIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const DocumentGeneration = () => {
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Upload states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    name: '',
    description: '',
    category: 'other'
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Generation states
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [extractedPlaceholders, setExtractedPlaceholders] = useState([]);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [generating, setGenerating] = useState(false);

  // Saved data states
  const [savedDataSets, setSavedDataSets] = useState([]);
  const [selectedDataSet, setSelectedDataSet] = useState('');
  const [dataSetName, setDataSetName] = useState('');
  const [saveData, setSaveData] = useState(false);
  const [loadingSavedData, setLoadingSavedData] = useState(false);
  const [originalPlaceholderValues, setOriginalPlaceholderValues] = useState({});
  const [dataModified, setDataModified] = useState(false);

  // App ID states
  const [appId, setAppId] = useState('');
  const [fetchingApiData, setFetchingApiData] = useState(false);

  // Master Data states
  const [masterDataList, setMasterDataList] = useState([]);
  const [selectedMasterData, setSelectedMasterData] = useState('');
  const [fetchingMasterData, setFetchingMasterData] = useState(false);
  const [loadingMasterDataList, setLoadingMasterDataList] = useState(false);

  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const categories = [
    { value: 'trademark', label: 'Trademark' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'contract', label: 'Contract' },
    { value: 'letter', label: 'Letter' },
    { value: 'report', label: 'Report' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/user-documents/templates');
      if (response.data.success) {
        setTemplates(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadTemplate = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const formData = new FormData();
      formData.append('template', uploadFile);
      formData.append('name', uploadData.name);
      formData.append('description', uploadData.description);
      formData.append('category', uploadData.category);

      const response = await api.post('/user-documents/templates/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success || response.data.data) {
        setSuccess('Template uploaded successfully!');
        setShowUploadModal(false);
        setUploadData({ name: '', description: '', category: 'other' });
        setUploadFile(null);
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error uploading template:', error);
      setError(error.response?.data?.message || 'Failed to upload template');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await api.delete(`/user-documents/templates/${templateId}`);
      setSuccess('Template deleted successfully!');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      setError('Failed to delete template');
    }
  };

  const handleGenerateDocument = async (template) => {
    try {
      setSelectedTemplate(template);
      
      // Extract placeholders from template
      const placeholders = extractPlaceholdersFromTemplate(template);
      setExtractedPlaceholders(placeholders);
      
      // Initialize placeholder values
      const initialValues = {};
      placeholders.forEach(placeholder => {
        initialValues[placeholder] = '';
      });
      setPlaceholderValues(initialValues);
      
      // Fetch saved data sets for this template
      const response = await api.get(`/user-documents/saved-data/${template._id}`);
      
      if (response.data.success) {
        const savedData = response.data.data || [];
        setSavedDataSets(savedData);
        
         // Auto-load the first saved data set if available
         if (savedData.length > 0) {
           const firstDataSet = savedData[0];
           setSelectedDataSet(firstDataSet.dataSetName);
           
           // Load the first data set's values
           const dataResponse = await api.get(`/user-documents/saved-data/${template._id}/${encodeURIComponent(firstDataSet.dataSetName)}`);
           if (dataResponse.data.success && dataResponse.data.data) {
             const loadedValues = dataResponse.data.data.placeholderValues || {};
             setPlaceholderValues(loadedValues);
             setOriginalPlaceholderValues(JSON.parse(JSON.stringify(loadedValues))); // Deep copy
             setDataModified(false);
           }
         }
      }
      
      // Fetch master data list for selection
      await fetchMasterDataList();
      
      setShowGenerationModal(true);
    } catch (error) {
      console.error('Error preparing document generation:', error);
      setError('Failed to prepare document generation');
    }
  };

  // Fetch saved data sets for a template
  const fetchSavedDataSets = async (templateId) => {
    try {
      setLoadingSavedData(true);
      const response = await api.get(`/user-documents/saved-data/${templateId}`);
      
      if (response.data.success) {
        setSavedDataSets(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching saved data sets:', error);
      setSavedDataSets([]);
    } finally {
      setLoadingSavedData(false);
    }
  };

  // Load saved data set
  const handleLoadSavedData = async (dataSetName) => {
    if (!selectedTemplate || !dataSetName) return;

    try {
      setLoadingSavedData(true);
      const response = await api.get(`/user-documents/saved-data/${selectedTemplate._id}/${encodeURIComponent(dataSetName)}`);
      
      if (response.data.success && response.data.data) {
        const loadedValues = response.data.data.placeholderValues || {};
        setPlaceholderValues(loadedValues);
        setOriginalPlaceholderValues(JSON.parse(JSON.stringify(loadedValues))); // Deep copy
        setSelectedDataSet(dataSetName);
        setDataModified(false);
        setSuccess(`Loaded data set: ${dataSetName}`);
      }
    } catch (error) {
      console.error('Error loading saved data set:', error);
      setError('Failed to load saved data set');
    } finally {
      setLoadingSavedData(false);
    }
  };

  // Update existing saved data set
  const handleUpdateSavedData = async () => {
    if (!selectedTemplate || !selectedDataSet) return;

    if (!window.confirm(`Are you sure you want to update the saved data set "${selectedDataSet}"?`)) {
      return;
    }

    try {
      setLoadingSavedData(true);
      
      // Update the saved data set with current placeholder values
      const response = await api.put(
        `/user-documents/saved-data/${selectedTemplate._id}/${encodeURIComponent(selectedDataSet)}`,
        { placeholderValues }
      );
      
      if (response.data.success) {
        setSuccess(`Updated data set: ${selectedDataSet}`);
        setOriginalPlaceholderValues(JSON.parse(JSON.stringify(placeholderValues)));
        setDataModified(false);
        
        // Refresh saved data sets to update usage count
        await fetchSavedDataSets(selectedTemplate._id);
      }
    } catch (error) {
      console.error('Error updating saved data set:', error);
      setError('Failed to update saved data set');
    } finally {
      setLoadingSavedData(false);
    }
  };

  // Delete saved data set
  const handleDeleteSavedData = async (dataSetName) => {
    if (!selectedTemplate || !dataSetName) return;
    
    if (!window.confirm(`Are you sure you want to delete the saved data set "${dataSetName}"?`)) {
      return;
    }

    try {
      await api.delete(`/user-documents/saved-data/${selectedTemplate._id}/${encodeURIComponent(dataSetName)}`);
      setSuccess(`Deleted data set: ${dataSetName}`);
      
      // Refresh saved data sets
      await fetchSavedDataSets(selectedTemplate._id);
      
      // Clear selection if deleted data set was selected
      if (selectedDataSet === dataSetName) {
        setSelectedDataSet('');
        setDataModified(false);
        setOriginalPlaceholderValues({});
      }
    } catch (error) {
      console.error('Error deleting saved data set:', error);
      setError('Failed to delete saved data set');
    }
  };

  // Fetch master data list when modal opens
  const fetchMasterDataList = async () => {
    try {
      setLoadingMasterDataList(true);
      const response = await api.get('/user-documents/master-data-list');
      
      if (response.data.success) {
        setMasterDataList(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching master data list:', error);
      setMasterDataList([]);
    } finally {
      setLoadingMasterDataList(false);
    }
  };

  // Helper function to get nested value from object, supporting array indices
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => {
      if (current === undefined || current === null) return undefined;
      
      // Check if the key contains an array index (e.g., "authorizedSignatories0")
      const arrayMatch = key.match(/^([a-zA-Z_$][a-zA-Z0-9_$]*)(\d+)$/);
      if (arrayMatch) {
        const arrayName = arrayMatch[1];
        const index = parseInt(arrayMatch[2], 10);
        return current[arrayName]?.[index];
      }
      
      return current[key];
    }, obj);
  };

  // Fetch and fill data from API
  const handleFillWithApiData = async () => {
    if (!appId.trim()) {
      setError('Please enter an App ID');
      return;
    }

    try {
      setFetchingApiData(true);
      setError('');
      
      // Fetch data from the backend proxy endpoint (to avoid CORS)
      const response = await api.get(`/user-documents/fetch-app-data/${appId.trim()}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch API data');
      }
      
      const apiData = response.data.data;
      console.log('API Response:', apiData);
      
      // Map API response to placeholder values
      const newPlaceholderValues = { ...placeholderValues };
      
      // Define mapping between API fields and template placeholders
      const fieldMapping = {
        'trademark.appno': apiData.appno,
        'trademark.applicationNumber': apiData.appno,
        'trademark.dateOfApp': apiData.dateOfApp,
        'trademark.applicationDate': apiData.dateOfApp,
        'trademark.class': apiData.class,
        'trademark.status': apiData.status,
        'trademark.state': apiData.state,
        'trademark.country': apiData.country,
        'trademark.tmAppliedFor': apiData.tmAppliedFor,
        'trademark.name': apiData.tmAppliedFor,
        'trademark.userDetail': apiData.userDetail,
        'trademark.validUpto': apiData.validUpto,
        'trademark.propName': apiData.propName,
        'trademark.proprietorName': apiData.propName,
        'trademark.propAddress': apiData.propAddress,
        'trademark.proprietorAddress': apiData.propAddress,
        'trademark.agentName': apiData.agentName,
        'trademark.buisnessName': apiData.buisnessName,
        'trademark.businessName': apiData.buisnessName,
        'trademark.buisnessType': apiData.buisnessType,
        'trademark.businessType': apiData.buisnessType,
        'trademark.goodsAndSerice': apiData.goodsAndSerice,
        'trademark.goodsAndServices': apiData.goodsAndSerice,
        'trademark.goods': apiData.goodsAndSerice,
        'trademark.tmcategory': apiData.tmcategory,
        'trademark.category': apiData.tmcategory,
        'trademark.tmtype': apiData.tmtype,
        'trademark.type': apiData.tmtype,
        'trademark.restr': apiData.restr,
        'trademark.restriction': apiData.restr,
        'trademark.jnumber': apiData.jnumber,
        'trademark.journalNumber': apiData.jnumber,
        'trademark.pubdate': apiData.pubdate,
        'trademark.publicationDate': apiData.pubdate,
        'trademark.JournalNo': apiData.JournalNo,
        'trademark.JournalDate': apiData.JournalDate,
        'trademark.regdate': apiData.regdate,
        'trademark.registrationDate': apiData.regdate,
        'trademark.DateofHearing': apiData.DateofHearing,
        'trademark.hearingDate': apiData.DateofHearing,
        'trademark.oppdate': apiData.oppdate,
        'trademark.oppositionDate': apiData.oppdate,
        'trademark.oppnum': apiData.oppnum,
        'trademark.oppositionNumber': apiData.oppnum,
        'trademark.oppcode': apiData.oppcode,
        'trademark.oppositionCode': apiData.oppcode,
        'trademark.oppname': apiData.oppname,
        'trademark.oppositionName': apiData.oppname,
        'trademark.opponent': apiData.oppname,
        'trademark.updatedate': apiData.updatedate,
        'trademark.lastUpdated': apiData.updatedate
      };
      
      // Auto-fill matching placeholders
      extractedPlaceholders.forEach(placeholder => {
        if (fieldMapping[placeholder] !== undefined && fieldMapping[placeholder] !== null) {
          newPlaceholderValues[placeholder] = fieldMapping[placeholder];
        }
      });
      
      setPlaceholderValues(newPlaceholderValues);
      setDataModified(true);
      setSuccess(`Successfully filled data from App ID: ${appId}`);
      
    } catch (error) {
      console.error('Error fetching API data:', error);
      setError('Failed to fetch data from API. Please check the App ID and try again.');
    } finally {
      setFetchingApiData(false);
    }
  };

  // Fetch and fill data from Master Data
  const handleFillWithMasterData = async () => {
    if (!selectedMasterData) {
      setError('Please select a Master Data record');
      return;
    }

    try {
      setFetchingMasterData(true);
      setError('');
      
      // Fetch full master data with populated fields
      const response = await api.get(`/user-documents/master-data/${selectedMasterData}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch Master Data');
      }
      
      const masterData = response.data.data;
      console.log('Master Data Response:', masterData);
      
      // Map Master Data to placeholder values
      const newPlaceholderValues = { ...placeholderValues };
      
      // Auto-fill placeholders that start with 'masterdata.' or 'masterData.'
      extractedPlaceholders.forEach(placeholder => {
        // Check if placeholder starts with masterdata. (case insensitive)
        const lowerPlaceholder = placeholder.toLowerCase();
        if (lowerPlaceholder.startsWith('masterdata.')) {
          // Extract the field path after 'masterdata.'
          const fieldPath = placeholder.substring(11); // Remove 'masterdata.'
          
          // Get the value from master data object
          const value = getNestedValue(masterData, fieldPath);
          
          if (value !== undefined && value !== null) {
            // Convert value to string if it's an object or array
            if (typeof value === 'object') {
              newPlaceholderValues[placeholder] = JSON.stringify(value);
            } else {
              newPlaceholderValues[placeholder] = String(value);
            }
          }
        }
      });
      
      setPlaceholderValues(newPlaceholderValues);
      setDataModified(true);
      setSuccess(`Successfully filled data from Master Data: ${masterData.clientName || masterData.companyName || 'Record'}`);
      
    } catch (error) {
      console.error('Error fetching Master Data:', error);
      setError('Failed to fetch Master Data. Please try again.');
    } finally {
      setFetchingMasterData(false);
    }
  };

  const extractPlaceholdersFromTemplate = (template) => {
    const placeholders = new Set();
    
    // Extract from extractedFields if available
    if (template.extractedFields && template.extractedFields.length > 0) {
      template.extractedFields.forEach(field => {
        if (field.fieldName) {
          placeholders.add(field.fieldName);
        }
      });
    }
    
    return Array.from(placeholders);
  };

  const handlePlaceholderValueChange = (placeholder, value) => {
    setPlaceholderValues(prev => {
      const newValues = {
        ...prev,
        [placeholder]: value
      };
      
      // Check if data has been modified from the original loaded values
      if (selectedDataSet && Object.keys(originalPlaceholderValues).length > 0) {
        const isModified = JSON.stringify(newValues) !== JSON.stringify(originalPlaceholderValues);
        setDataModified(isModified);
      }
      
      return newValues;
    });
  };

  const handleSubmitGeneration = async (e) => {
    e.preventDefault();
    
    try {
      setGenerating(true);
      setError('');

      // Prepare the data for document generation
      const generationData = {
        templateId: selectedTemplate._id,
        placeholderValues: placeholderValues,
        fileName: `${selectedTemplate.name}_${Date.now()}`,
        saveData: saveData,
        dataSetName: dataSetName.trim()
      };

      const response = await api.post('/user-documents/generate', generationData);

      if (response.data.success) {
        setSuccess('Document generated successfully!');
        
        // Download the generated document properly
        if (response.data.data && response.data.data.fileName) {
          await downloadGeneratedDocument(response.data.data.fileName);
        }
        
        setShowGenerationModal(false);
        setSelectedTemplate(null);
        setExtractedPlaceholders([]);
        setPlaceholderValues({});
        setSavedDataSets([]);
        setSelectedDataSet('');
        setDataSetName('');
        setSaveData(false);
      }
    } catch (error) {
      console.error('Error generating document:', error);
      setError(error.response?.data?.message || 'Failed to generate document');
    } finally {
      setGenerating(false);
    }
  };

  const downloadGeneratedDocument = async (fileName) => {
    try {
      // Make a request to get the file as a blob
      const response = await api.get(`/user-documents/download/${fileName}`, {
        responseType: 'blob', // Important: This tells axios to handle the response as a binary blob
      });

      // Create a blob from the response data
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element and trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName; // This forces the download instead of navigation
      document.body.appendChild(link);
      link.click();

      // Clean up: remove the link and revoke the blob URL
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`Document ${fileName} downloaded successfully`);
    } catch (error) {
      console.error('Error downloading document:', error);
      setError('Failed to download generated document');
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Document Generation</h1>
        <p className="text-gray-600">Upload templates and generate documents with dynamic placeholders</p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
            <button onClick={clearMessages} className="ml-auto text-green-400 hover:text-green-600">
              ×
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button onClick={clearMessages} className="ml-auto text-red-400 hover:text-red-600">
              ×
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Templates
          </button>
          <button
            onClick={() => setActiveTab('generate')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'generate'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Generate Documents
          </button>
        </nav>
      </div>

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div>
          {/* Upload Button */}
          <div className="mb-6">
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
              Upload Template
            </button>
          </div>

          {/* Search and Filter */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
                />
              </div>
            </div>
            <div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Templates List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading templates...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-8">
              <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No templates found</p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="mt-2 text-blue-600 hover:text-blue-800"
              >
                Upload your first template
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplates.map((template) => (
                <div key={template._id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-1">{template.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {categories.find(c => c.value === template.category)?.label || template.category}
                      </span>
                    </div>
                  </div>

                  {/* Template Info */}
                  <div className="mb-4 text-sm text-gray-600">
                    <p>File: {template.originalFileName}</p>
                    <p>Placeholders: {template.extractedFields?.length || 0}</p>
                    <p>Created: {new Date(template.createdAt).toLocaleDateString()}</p>
                  </div>

                  {/* Placeholders Preview */}
                  {template.extractedFields && template.extractedFields.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Placeholders:</p>
                      <div className="flex flex-wrap gap-1">
                        {template.extractedFields.slice(0, 3).map((field, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                            {field.fieldName}
                          </span>
                        ))}
                        {template.extractedFields.length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{template.extractedFields.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-between items-center">
                    <button
                      onClick={() => handleGenerateDocument(template)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <DocumentTextIcon className="h-4 w-4 mr-1" />
                      Generate
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template._id)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Generate Documents Tab */}
      {activeTab === 'generate' && (
        <div>
          <div className="text-center py-8">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Generate Documents</h3>
            <p className="text-gray-600 mb-4">Select a template from the Templates tab to generate a document</p>
            <button
              onClick={() => setActiveTab('templates')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              View Templates
            </button>
          </div>
        </div>
      )}

      {/* Upload Template Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Template</h3>
              
              <form onSubmit={handleUploadTemplate}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={uploadData.name}
                    onChange={(e) => setUploadData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter template name"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={uploadData.description}
                    onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Enter template description"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    required
                    value={uploadData.category}
                    onChange={(e) => setUploadData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template File (.docx) *
                  </label>
                  <input
                    type="file"
                    required
                    accept=".docx"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Use placeholders in format: [%%PLACEHOLDER_NAME%%]
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Document Generation Modal */}
      {showGenerationModal && selectedTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Generate Document: {selectedTemplate.name}
              </h3>
              
              {/* App ID Section */}
              <div className="mb-6 pb-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  App ID (Auto-fill from API)
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={appId}
                    onChange={(e) => setAppId(e.target.value)}
                    placeholder="Enter App ID (e.g., 4371808)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={handleFillWithApiData}
                    disabled={fetchingApiData || !appId.trim()}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {fetchingApiData ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Fetching...
                      </>
                    ) : (
                      <>
                        <DocumentArrowUpIcon className="h-4 w-4 mr-2" />
                        Fill With API Data
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter an App ID to automatically fill trademark data from the external API
                </p>
              </div>
              
              {/* Master Data Section */}
              <div className="mb-6 pb-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Master Data (Auto-fill from Database)
                </label>
                <div className="flex space-x-2">
                  <select
                    value={selectedMasterData}
                    onChange={(e) => setSelectedMasterData(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    disabled={loadingMasterDataList}
                  >
                    <option value="">Select Master Data...</option>
                    {masterDataList.map((data) => (
                      <option key={data._id} value={data._id}>
                        {data.clientName || data.companyName || `Record #${data.sequence}`}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleFillWithMasterData}
                    disabled={fetchingMasterData || !selectedMasterData}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {fetchingMasterData ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Fetching...
                      </>
                    ) : (
                      <>
                        <DocumentTextIcon className="h-4 w-4 mr-2" />
                        AutoFill
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select a Master Data record to automatically fill fields like masterdata.clientName, masterdata.clientType.name, etc.
                </p>
              </div>
              
              <form onSubmit={handleSubmitGeneration}>
                {extractedPlaceholders.length > 0 ? (
                  <div className="mb-6">
                    <p className="text-sm text-gray-600 mb-4">
                      Fill in the values for the placeholders found in the template:
                    </p>
                    
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {extractedPlaceholders.map((placeholder, index) => (
                        <div key={index}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {placeholder}
                          </label>
                          <input
                            type="text"
                            value={placeholderValues[placeholder] || ''}
                            onChange={(e) => handlePlaceholderValueChange(placeholder, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            placeholder={`Enter value for ${placeholder}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-700">
                      No placeholders found in this template. The document will be generated as-is.
                    </p>
                  </div>
                )}

                {/* Saved Data Sets Section */}
                <div className="mb-6 border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Auto-Fill Options</h4>
                  
                  {/* Load Saved Data */}
                  {savedDataSets.length > 0 && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Load Saved Data Set
                      </label>
                      <div className="flex space-x-2">
                        <select
                          value={selectedDataSet}
                          onChange={(e) => {
                            setSelectedDataSet(e.target.value);
                            if (e.target.value) {
                              handleLoadSavedData(e.target.value);
                            }
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          disabled={loadingSavedData}
                        >
                          <option value="">Select a saved data set...</option>
                          {savedDataSets.map((dataSet, index) => (
                            <option key={index} value={dataSet.dataSetName}>
                              {dataSet.dataSetName} (used {dataSet.usageCount} times)
                            </option>
                          ))}
                        </select>
                        {selectedDataSet && (
                          <button
                            type="button"
                            onClick={() => handleDeleteSavedData(selectedDataSet)}
                            className="px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50"
                            title="Delete saved data set"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {loadingSavedData && (
                        <p className="text-sm text-gray-500 mt-1">Loading saved data...</p>
                      )}
                      
                      {/* Update button when data is modified */}
                      {selectedDataSet && dataModified && (
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={handleUpdateSavedData}
                            disabled={loadingSavedData}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                          >
                            <PencilIcon className="h-4 w-4 mr-2" />
                            Update "{selectedDataSet}"
                          </button>
                          <p className="text-xs text-gray-500 mt-1">
                            You've modified the loaded data. Click to save changes to "{selectedDataSet}".
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Save Current Data */}
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="saveData"
                        checked={saveData}
                        onChange={(e) => setSaveData(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="saveData" className="ml-2 block text-sm text-gray-700">
                        Save this data for future use
                      </label>
                    </div>
                    
                    {saveData && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Data Set Name
                        </label>
                        <input
                          type="text"
                          value={dataSetName}
                          onChange={(e) => setDataSetName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter a name for this data set (e.g., 'Client ABC', 'Project XYZ')"
                          required={saveData}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          This will save all the placeholder values for quick reuse next time.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowGenerationModal(false);
                      setSelectedTemplate(null);
                      setExtractedPlaceholders([]);
                      setPlaceholderValues({});
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={generating}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    {generating ? 'Generating...' : 'Generate Document'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentGeneration; 



